import { Typography, useTheme } from '@mui/material';
import { Layer, Popup, Source } from '@vis.gl/react-maplibre';
import { Fragment } from 'react';

import { PopupContent } from '@/components/PopupContent';
import { PopupDatePeriod } from '@/components/popup/PopupDatePeriod';
import { PopupTitle } from '@/components/popup/PopupTitle';
import { RoutePropertyService } from '@/modules/RoutePropertyService';
import type { StrategyFeature } from '@/types/StrategyFeature';
import { StrategyLayerType } from '@/types/StrategyLayerType';

interface StrategyPopupProps {
  feature?: StrategyFeature;
  onClose: () => void;
}

export function StrategyPopup({ feature, onClose }: StrategyPopupProps) {
  const theme = useTheme();

  if (!feature) {
    return null;
  }

  const [longitude, latitude] = feature.geometry.coordinates;

  const description = feature.properties.comments?.replace('{}', '').replace('{"', '').replace('"}', '');

  const routeAvoidMatched =
    feature.properties.route_avoid_matched && JSON.parse(feature.properties.route_avoid_matched);
  const routeMatched = feature.properties.route_matched && JSON.parse(feature.properties.route_matched);

  return (
    <Fragment>
      <Popup
        key={feature.properties.id}
        closeButton={false}
        latitude={latitude}
        longitude={longitude}
        maxWidth="min(480px, 75vw)"
        offset={18}
        onClose={onClose}
      >
        <PopupContent onClose={onClose}>
          <PopupTitle>{feature.properties.type}</PopupTitle>
          <PopupDatePeriod feature={feature} />

          {description ? <Typography>{description}</Typography> : null}
        </PopupContent>
      </Popup>

      <Source data={RoutePropertyService.getAlternativeStrategyGeoJson(routeAvoidMatched, routeMatched)} type="geojson">
        <Layer
          beforeId={StrategyLayerType.ALTERNATIVE_ROUTING}
          type="line"
          layout={{
            'line-cap': 'round',
          }}
          paint={{
            'line-color': 'white',
            'line-width': ['interpolate', ['exponential', 0.5], ['zoom'], 10, 6, 14, 8, 18, 12],
          }}
        />
        <Layer
          beforeId={StrategyLayerType.ALTERNATIVE_ROUTING}
          type="line"
          layout={{
            'line-cap': 'round',
          }}
          paint={{
            'line-color': [
              'case',
              ['==', ['get', 'type'], 'strategy-avoid'],
              theme.palette.error.main,
              ['==', ['get', 'type'], 'traffic-report'],
              theme.palette.error.main,
              theme.palette.success.main,
            ],
            'line-width': ['interpolate', ['exponential', 0.5], ['zoom'], 10, 2, 14, 4, 18, 8],
          }}
        />
      </Source>
    </Fragment>
  );
}
